import React from 'react'
import { toMoney } from '@mobi/utils/money'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { spacing } from '@mobi/component-library/Foundations'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { formatKambiCurrencyToDollarDecimal } from '@core/Areas/KambiSports/helpers/currencyFormat'
import { getEventLink } from '@core/Areas/KambiSports/helpers/navigation'
import { SportsIcon } from '@core/Areas/KambiSports/Components/Common/SportsIcon'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Components/BetHistory/context'
import { CountdownStatus } from '../Common/CountdownStatus'
import { LegStatusIcon } from '../Common/LegStatusIcon'
import { LegsProgressStatus } from '../Common/LegsProgressStatus'
import { TeamIcon } from '@core/Areas/KambiSports/Components/BetHistory/BetInfo/Components//Common/TeamsIcon'

export const SameGameMulti: React.FC<{
  legCount: number
  event: BetLoopKambiShareCoupon['events'][0]
  outcomes: BetLoopKambiShareCoupon['outcomes']
  betOffers: BetLoopKambiShareCoupon['betOffers']
  playedOdds: BetLoopKambiShareCoupon['couponRows'][0]['playedOdds']
  betStatus:
    | BetLoopKambiShareCoupon['bets'][0]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
  isWithinMultiBet?: boolean
  onMultiLegCountdownReachingZero?(): void
}> = ({
  legCount,
  event,
  outcomes,
  betOffers,
  playedOdds,
  betStatus,
  isWithinMultiBet,
  onMultiLegCountdownReachingZero,
}) => {
  const instanceId = React.useId()
  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)

  const isBetLoopQuickShare = couponDisplayContext.type === 'betloop-quick-share'

  const titleText = `${legCount} Leg Same Game Multi`
  const odds = playedOdds && toMoney(formatKambiCurrencyToDollarDecimal(playedOdds))
  const sportName = event.sport
  const hasOpenOutcomes = outcomes.some(outcome => outcome.status === 'OPEN')
  const leagueId = event.eventGroups?.[event?.eventGroups.length - 1]?.id || 0

  return (
    <>
      <BetCardTitle
        title={titleText}
        rightComponent={
          <Flex gap={spacing.smx2}>
            {odds}

            {isBetLoopQuickShare && (
              <RadioButton
                size='md'
                type='Tick'
                id={instanceId}
                isChecked={couponDisplayContext.data.isSelected}
              />
            )}
          </Flex>
        }
        bottomLeftComponent={<LegsProgressStatus outcomes={outcomes} events={[event]} />}
      />

      <BetCard.EventDetails
        iconComponent={<SportsIcon sport={sportName} />}
        heading={event.eventName}
        statusComponent={
          ((hasOpenOutcomes && isWithinMultiBet) || !isWithinMultiBet) && (
            <CountdownStatus
              displayType={isWithinMultiBet ? 'multi-leg' : 'standard'}
              eventStartTime={event.eventStartDate}
              betStatus={betStatus}
              onCountdownReachingZero={onMultiLegCountdownReachingZero}
            />
          )
        }
      />

      {outcomes.map(outcome => {
        const betOffer = betOffers.find(b => b.betOfferId === outcome.betOfferId)

        return (
          <BetCard.SelectionInfo
            key={outcome.betOfferId}
            heading={outcome.label}
            subHeading={betOffer?.criterion}
            linkInfo={{ pathname: getEventLink(event.eventId) }}
            rightAlignedComponent={<LegStatusIcon status={outcome.status} />}
            iconComponent={
              <TeamIcon
                leagueId={leagueId}
                participationId={outcome.participantId}
                defaultIcon={<SportsIcon sport={sportName} size='2rem' />}
                size='2rem'
              />
            }
          />
        )
      })}
    </>
  )
}
